import type { CreateAddressInput, CreateCustomerInput, OrderFragment, OrderLineFragment, PaymentInput, SearchProductVariantResultFragment, ShippingMethodFragment, ShippingMethodQuoteFragment } from '#graphql-operations'
import type { AddItemToOrderVariables, OrderState } from '@layers/common/app/services/activeOrder'
import { defineQuery, useQuery } from '#imports'
import { activeOrder as $activeOrder, addItemToOrder as $addItemToOrder, addPaymentToOrder as $addPaymentToOrder, adjustOrderLine as $adjustOrderLine, applyCouponCode as $applyCouponCode, nextOrderStates as $nextOrderStates, removeCouponCode as $removeCouponCode, removeOrderLine as $removeOrderLine, setCustomerForOrder as $setCustomerForOrder, setIntermediatePaymentMethod as $setIntermediatePaymentMethod, setOrderBillingAddress as $setOrderBillingAddress, setOrderShippingAddress as $setOrderShippingAddress, setOrderShippingMethod as $setOrderShippingMethod, transitionOrderToState as $transitionOrderToState } from '@layers/common/app/services/activeOrder'
import { OrderModificationError } from '@layers/common/app/services/errors'
import { defineStore } from 'pinia'

export const useActiveOrderQuery = defineQuery(() => {
  // MUST start false, including on the server. `!import.meta.client` evaluates
  // to TRUE during SSR, which enables the query below and fires activeOrder on
  // every server render. Only the client branch is meant to flip it.
  const isReady = ref(false)

  if (import.meta.client) {
    // Wait for app mount to avoid blocking LCP
    const nuxtApp = useNuxtApp()
    const queryCache = useQueryCache()

    // The app runs its own lazy `useAsyncData('activeOrder')` at hydration (the
    // legacy `$activeOrder` cache), which asks the server the exact same
    // question this query does. Read it generically by key so this layer keeps
    // no dependency on the app plugin: `undefined` means it has not resolved or
    // it failed, `null` is a real answer meaning "no active order".
    const { data: legacyActiveOrder } = useNuxtData<OrderFragment | null>('activeOrder')

    // Seed, don't fetch. Without this, enabling the query below fired a second
    // identical activeOrder request on every page load, and activeOrder is
    // NON_CACHEABLE (100% origin miss) — plus an open cart runs ~24 SQL
    // statements of gift evaluation per response. `setQueryData` stamps the
    // entry's `when` with `Date.now()`, so the entry is inside `staleTime` when
    // `enabled` flips and Pinia Colada's enabled-watcher calls `refresh()`,
    // which skips the network for fresh data. Everything else is untouched:
    // explicit `refetchActiveOrder()` calls use `fetch()` and always hit the
    // server, and the entry goes stale on the usual schedule. If the legacy
    // fetch has not resolved (or errored, which leaves its data `undefined`),
    // or Colada already has data from a mutation, do nothing and let the query
    // fetch as before.
    const seedFromLegacyCache = () => {
      if (queryCache.getQueryData(['activeOrder']) === undefined && legacyActiveOrder.value !== undefined)
        queryCache.setQueryData(['activeOrder'], legacyActiveOrder.value)
    }

    nuxtApp.hook('app:mounted', () => {
      // Use longer delay to ensure all critical resources load first
      setTimeout(() => {
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(() => {
            seedFromLegacyCache()
            isReady.value = true
          }, { timeout: 5000 })
        }
        else {
          seedFromLegacyCache()
          isReady.value = true
        }
      }, 1000) // Wait 1 second after mount
    })
  }

  const { state, refetch, ...rest } = useQuery<OrderFragment | null>({
    key: ['activeOrder'],
    enabled: () => isReady.value,
    query: () => $activeOrder(),
  })

  return { activeOrder: state, refetchActiveOrder: refetch, ...rest }
})

export const useActiveOrderStore = defineStore('activeOrder', () => {
  const queryCache = useQueryCache()
  const { activeOrder, refetchActiveOrder } = useActiveOrderQuery()

  // Shared by every product card: any size of this colour counts as in cart.
  const cartProductIds = computed(() => {
    const order = activeOrder.value.data
    return new Set(order?.active
      ? order.lines.filter(line => line.quantity > 0).map(line => line.productVariant.product.id)
      : [])
  })

  const transitionOrderToState = async (nextState: OrderState) => {
    if (!activeOrder.value.data || activeOrder.value.data.state === nextState)
      return

    const nextOrderStates = await $nextOrderStates()

    // The state names are for the console only. What is thrown is the same
    // ORDER_MODIFICATION_ERROR a refused mutation carries, so callers have a
    // code to branch on instead of an English sentence — and `useCartAdd`
    // recognises it: the usual cause is a cached state that is out of date, so
    // it refetches once and retries, and the retry finds AddingItems and goes
    // straight to the mutation. A warning rather than an error for that reason:
    // this is often recovered from a moment later.
    if (!nextOrderStates.includes(nextState)) {
      console.warn(`Cannot transition order from ${activeOrder.value.data?.state} to ${nextState}; available states: ${nextOrderStates.join(', ')}`)
      throw new OrderModificationError('The order cannot be modified in its current state')
    }

    queryCache.setQueryData(['activeOrder'], await $transitionOrderToState(nextState))
  }

  const addItemToOrder = async (productVariant: SearchProductVariantResultFragment, quantity = 1, variables: AddItemToOrderVariables = {}) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $addItemToOrder(productVariant.id.toString(), quantity, variables))
  }

  const removeOrderLine = async (line: OrderLineFragment) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $removeOrderLine(line.id.toString()))
  }

  const adjustOrderLine = async (line: OrderLineFragment, quantity: number) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $adjustOrderLine(line.id.toString(), quantity))
  }

  const setCustomerForOrder = async (input: CreateCustomerInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setCustomerForOrder(input))
    if (input.emailAddress)
      setFaroUser({ email: input.emailAddress })
  }

  const setOrderShippingMethod = async (shippingMethod: ShippingMethodQuoteFragment | ShippingMethodFragment) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderShippingMethod(shippingMethod.id.toString()))
  }

  const setOrderShippingAddress = async (address: CreateAddressInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderShippingAddress(address))
  }

  const setOrderBillingAddress = async (address: CreateAddressInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderBillingAddress(address))
  }

  const setIntermediatePaymentMethod = async (paymentMethodCode: string) => {
    if (activeOrder.value.data?.customFields?.intermediatePaymentMethod === paymentMethodCode)
      return
    queryCache.setQueryData(['activeOrder'], await $setIntermediatePaymentMethod(paymentMethodCode))
  }

  const applyCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $applyCouponCode(couponCode))
  }

  const removeCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $removeCouponCode(couponCode))
  }

  const addPaymentToOrder = async (input: PaymentInput) => {
    await transitionOrderToState('ArrangingPayment')
    queryCache.setQueryData(['activeOrder'], await $addPaymentToOrder(input))
  }

  const orderSurcharges = computed(() => {
    return activeOrder.value.data?.surcharges?.filter(surcharge => surcharge.orderLineId == null) || []
  })

  const orderTotal = computed(() => {
    return activeOrder.value.data?.total || 0
  })

  const currency = computed(() => {
    return activeOrder.value.data?.currency || 'USD'
  })

  // Faro has no tags. Session attributes are the nearest equivalent, and
  // `setFaroSessionAttributes` merges rather than replaces so this does not
  // clobber the locale attribute the Faro plugin sets on the same session.
  watch(() => activeOrder.value.data?.code, (orderCode) => {
    if (orderCode) {
      setFaroSessionAttributes({ orderCode })
    }
  })

  return {
    activeOrder,
    cartProductIds,
    refetchActiveOrder,
    orderSurcharges,
    orderTotal,
    currency,
    transitionOrderToState,
    addItemToOrder,
    removeOrderLine,
    adjustOrderLine,
    setCustomerForOrder,
    setOrderShippingMethod,
    setOrderShippingAddress,
    setOrderBillingAddress,
    setIntermediatePaymentMethod,
    applyCouponCode,
    removeCouponCode,
    addPaymentToOrder,
  }
})
